import type { Route } from '../router.ts';
import { navigate } from '../router.ts';
import { icons } from '../icons.ts';
import { loadSettings, saveSettings, type Dialect } from '../data/stores.ts';

interface NavItem { route: Route; label: string; icon: string; }

const items: NavItem[] = [
  { route: 'accueil',       label: 'Accueil',                icon: icons.home },
  { route: 'parcours',      label: 'Mon parcours',           icon: icons.path },
  { route: 'vocabulaire',   label: 'Vocabulaire',            icon: icons.vocab },
  { route: 'grammaire',     label: 'Grammaire en contexte',  icon: icons.grammar },
  { route: 'prononciation', label: 'Prononciation',          icon: icons.pron },
  { route: 'ecoute',        label: 'Écoute',                 icon: icons.listen },
  { route: 'tache',         label: 'Tâche de la semaine',    icon: icons.task },
  { route: 'acquis',        label: 'Mes acquis',             icon: icons.check },
  { route: 'parametres',    label: 'Paramètres',             icon: icons.settings },
];

export function renderSidebar(active: Route): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'flex flex-col gap-2 p-4 border-r border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)]';
  nav.style.gridArea = 'nav';

  const logo = document.createElement('a');
  logo.href = '#/accueil';
  logo.className = 'flex items-center gap-2 mb-6 mt-2 nav-collapsed-center';
  logo.setAttribute('aria-label', 'ParleNorvégien — accueil');
  logo.innerHTML = `
    <svg viewBox="0 0 80 50" class="w-8 h-5 flex-shrink-0" aria-hidden="true">
      <defs>
        <clipPath id="sb-l"><path d="M2 6 Q2 0 8 0 L30 0 Q36 0 36 6 L36 32 Q36 38 30 38 L20 38 L14 46 L18 38 L8 38 Q2 38 2 32 Z"/></clipPath>
        <clipPath id="sb-r"><path d="M42 10 Q42 4 48 4 L70 4 Q76 4 76 10 L76 36 Q76 42 70 42 L66 42 L70 50 L60 42 L48 42 Q42 42 42 36 Z"/></clipPath>
      </defs>
      <g clip-path="url(#sb-l)">
        <rect x="2"  y="0" width="11" height="44" fill="#002654"/>
        <rect x="13" y="0" width="11" height="44" fill="#ffffff"/>
        <rect x="24" y="0" width="12" height="44" fill="#ED2939"/>
      </g>
      <g clip-path="url(#sb-r)">
        <rect x="42" y="4" width="34" height="42" fill="#BA0C2F"/>
        <rect x="54" y="4" width="8" height="42" fill="#ffffff"/>
        <rect x="42" y="20" width="34" height="8" fill="#ffffff"/>
        <rect x="56" y="4" width="4" height="42" fill="#00205B"/>
        <rect x="42" y="22" width="34" height="4" fill="#00205B"/>
      </g>
    </svg>
    <span class="font-extrabold text-base tracking-tight whitespace-nowrap nav-label">
      <span style="color:#0E2849">Parle</span><span style="color:#E13A3E">Norvégien</span>
    </span>
  `;
  logo.addEventListener('click', (e) => { e.preventDefault(); navigate('accueil'); });
  nav.append(logo);

  for (const item of items) {
    const a = document.createElement('a');
    a.href = `#/${item.route}`;
    a.className = [
      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors nav-collapsed-center',
      item.route === active
        ? 'bg-[color:var(--color-fjord-100)] text-[color:var(--color-fjord-700)] font-medium'
        : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-fjord-50)] hover:text-[color:var(--color-ink)]',
    ].join(' ');
    a.innerHTML = `<span class="w-4 h-4 inline-block opacity-70 flex-shrink-0">${item.icon}</span><span class="nav-label">${item.label}</span>`;
    a.addEventListener('click', (e) => { e.preventDefault(); navigate(item.route); });
    nav.append(a);
  }

  const spacer = document.createElement('div');
  spacer.className = 'flex-1';
  nav.append(spacer);

  const langPair = document.createElement('div');
  langPair.className = 'flex items-center justify-center gap-2 text-sm text-[color:var(--color-ink-muted)] py-2 border-t border-[color:var(--color-border)] nav-label';
  langPair.innerHTML = `<span>🇫🇷 FR</span><span>⇌</span><span>🇳🇴 NO</span>`;
  nav.append(langPair);

  const dialect = document.createElement('div');
  dialect.className = 'flex flex-col gap-1 text-xs px-2 py-2 nav-label';
  dialect.innerHTML = `
    <label class="flex items-center gap-2 cursor-pointer">
      <input type="radio" name="dialect" value="bokmal" />
      <span>Bokmål standard</span>
    </label>
    <label class="flex items-center gap-2 cursor-pointer">
      <input type="radio" name="dialect" value="siddis" />
      <span>Siddis <span class="text-[10px] opacity-60">(dialecte de Stavanger)</span></span>
    </label>
  `;
  // Populate + wire async (don't block initial render)
  void loadSettings().then((s) => {
    const radios = dialect.querySelectorAll<HTMLInputElement>('input[name="dialect"]');
    for (const r of radios) {
      r.checked = r.value === s.dialect;
      r.addEventListener('change', () => {
        if (!r.checked) return;
        s.dialect = r.value as Dialect;
        void saveSettings(s);
      });
    }
  });
  nav.append(dialect);

  return nav;
}
